export interface ResourceState {
  value: number
  rechargeDelay: number
}

export interface StaggerState {
  value: number
  overloadedFor: number
}

export interface TargetCandidate {
  id: string
  distance: number
  alignment: number
  alive: boolean
}

export type MissionOutcome = 'victory' | 'defeat' | null

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export function advanceEnergy(
  state: ResourceState,
  deltaSeconds: number,
  drainPerSecond: number,
  rechargePerSecond: number,
  rechargeDelaySeconds: number,
  maximum = 100,
): ResourceState {
  if (drainPerSecond > 0 && state.value > 0) {
    return {
      value: clamp(state.value - drainPerSecond * deltaSeconds, 0, maximum),
      rechargeDelay: rechargeDelaySeconds,
    }
  }

  const delay = Math.max(0, state.rechargeDelay - deltaSeconds)
  return {
    value:
      delay > 0
        ? state.value
        : clamp(state.value + rechargePerSecond * deltaSeconds, 0, maximum),
    rechargeDelay: delay,
  }
}

export function applyStagger(
  state: StaggerState,
  impact: number,
  maximum = 100,
  overloadDuration = 2.2,
): StaggerState {
  if (state.overloadedFor > 0) return state
  const next = clamp(state.value + Math.max(0, impact), 0, maximum)
  return next >= maximum
    ? { value: maximum, overloadedFor: overloadDuration }
    : { ...state, value: next }
}

export function advanceStagger(
  state: StaggerState,
  deltaSeconds: number,
  decayPerSecond: number,
): StaggerState {
  if (state.overloadedFor > 0) {
    const overloadedFor = Math.max(0, state.overloadedFor - deltaSeconds)
    return overloadedFor === 0
      ? { value: 0, overloadedFor: 0 }
      : { value: state.value, overloadedFor }
  }
  return {
    value: Math.max(0, state.value - decayPerSecond * deltaSeconds),
    overloadedFor: 0,
  }
}

export function directHitMultiplier(stagger: StaggerState): number {
  return stagger.overloadedFor > 0 ? 1.65 : 1
}

export function chooseLockTarget(
  candidates: readonly TargetCandidate[],
  maximumDistance: number,
  minimumAlignment: number,
): string | null {
  const valid = candidates
    .filter(
      (candidate) =>
        candidate.alive &&
        candidate.distance <= maximumDistance &&
        candidate.alignment >= minimumAlignment,
    )
    .sort((a, b) => {
      const scoreA = a.distance * (2 - a.alignment)
      const scoreB = b.distance * (2 - b.alignment)
      return scoreA - scoreB
    })
  return valid[0]?.id ?? null
}

/**
 * Assault boost: the committed forward sprint.
 *
 * It escalates out of a held forward boost rather than taking its own key, so
 * it stays reachable on the existing control scheme. `charge` is the spool-up;
 * `engaged` is the committed state, which costs more drive and trades away
 * turn authority for speed.
 */
export interface AssaultBoostState {
  charge: number
  engaged: boolean
}

export interface AssaultBoostInput {
  boosting: boolean
  forward: boolean
  energy: number
  /** Staggered, overloaded, or slammed into something. */
  disrupted: boolean
}

/** Seconds of held forward boost before the assault drive commits. */
export const ASSAULT_BOOST_SPOOL_SECONDS = 0.55

export function advanceAssaultBoost(
  state: AssaultBoostState,
  deltaSeconds: number,
  input: AssaultBoostInput,
): AssaultBoostState {
  // Disruption resets the spool rather than merely dropping the commit: a
  // surviving charge would let the mech resume a sprint the instant it
  // recovered, which is most of the cost of being staggered.
  const sustainable = input.boosting && input.forward && input.energy > 0 && !input.disrupted
  if (!sustainable) return { charge: 0, engaged: false }

  const charge = state.charge + deltaSeconds
  return { charge, engaged: state.engaged || charge >= ASSAULT_BOOST_SPOOL_SECONDS }
}

/** Command units fight harder below half armour. One way, once. */
export function commandPhaseFor(armor: number, maximumArmor: number): 1 | 2 {
  if (maximumArmor <= 0) return 1
  return armor <= maximumArmor * 0.5 ? 2 : 1
}

export function evaluateMission(playerAlive: boolean, enemiesAlive: number): MissionOutcome {
  if (!playerAlive) return 'defeat'
  if (enemiesAlive <= 0) return 'victory'
  return null
}
